import type { HTMLAttributes, ReactNode } from 'react';

import type { IconDefinition } from '@fortawesome/fontawesome-common-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { cn } from '../../lib/utils';
import additionalRequestSrc from './icons/empty-state-additional-request.svg';
import announcementSrc from './icons/empty-state-announcement.svg';
import automationSrc from './icons/empty-state-automations.svg';
import connectivitySrc from './icons/empty-state-connectivity.svg';
import connectorSrc from './icons/empty-state-connector.svg';
import dashboardSrc from './icons/empty-state-dashboard.svg';
import documentSrc from './icons/empty-state-document.svg';
import genericSrc from './icons/empty-state-generic.svg';
import incidentSrc from './icons/empty-state-incident.svg';
import orderSrc from './icons/empty-state-order.svg';
import planningSrc from './icons/empty-state-planning.svg';
import referenceSrc from './icons/empty-state-reference.svg';
import shipmentBlockedSrc from './icons/empty-state-shipment-blocked.svg';
import shipmentMonitoredSrc from './icons/empty-state-shipment-monitored.svg';
import stockAlertSrc from './icons/empty-state-stock-alert.svg';
import stockAlertOkSrc from './icons/empty-state-stock-ok.svg';
import stockSrc from './icons/empty-state-stock.svg';
import transmissionSrc from './icons/empty-state-transmission.svg';
import transportSrc from './icons/empty-state-transport.svg';

const ICON_SRC = {
    'additional-request': additionalRequestSrc,
    announcement: announcementSrc,
    automation: automationSrc,
    connectivity: connectivitySrc,
    connector: connectorSrc,
    dashboard: dashboardSrc,
    document: documentSrc,
    generic: genericSrc,
    incident: incidentSrc,
    order: orderSrc,
    planning: planningSrc,
    reference: referenceSrc,
    shipmentBlocked: shipmentBlockedSrc,
    shipmentMonitored: shipmentMonitoredSrc,
    stock: stockSrc,
    stockAlert: stockAlertSrc,
    stockAlertOk: stockAlertOkSrc,
    transmission: transmissionSrc,
    transport: transportSrc,
} as const;

export type EmptyStateIconName = keyof typeof ICON_SRC;

export interface EmptyStateProps {
    icon?: EmptyStateIconName | IconDefinition;
    picture?: {
        src: string;
        width: number;
        height: number;
    };
    paragraph: string;
    dataTestid?: string;
    htmlAttributes?: HTMLAttributes<HTMLDivElement>;
    title?: string;
    children?: ReactNode;
    size?: 'default' | 'compact';
}

export const EmptyState = ({
    icon,
    picture,
    paragraph,
    dataTestid,
    htmlAttributes,
    title,
    children,
    size = 'default',
}: EmptyStateProps) => {
    const { className, ...restHtmlAttributes } = htmlAttributes ?? {};
    const isCompact = size === 'compact';

    return (
        <div
            className={cn('flex flex-col items-center gap-y-2', isCompact ? 'pb-0' : 'pb-8', className)}
            data-testid={dataTestid}
            data-component-name='EmptyState'
            {...restHtmlAttributes}
        >
            <div className={cn('flex shrink-0 items-center justify-center', isCompact && 'h-16')}>
                {typeof icon === 'string' ? (
                    <img
                        src={ICON_SRC[icon]}
                        alt=''
                        className={isCompact ? 'h-16 w-auto object-contain' : 'max-w-32'}
                    />
                ) : icon ? (
                    <FontAwesomeIcon
                        icon={icon}
                        className='text-3xl text-gray-500'
                    />
                ) : null}
                {picture && (
                    <img
                        src={picture.src}
                        width={picture.width}
                        height={picture.height}
                        alt=''
                        className={isCompact ? 'h-16 w-auto object-contain' : undefined}
                    />
                )}
            </div>
            {title && <p className='text-base font-semibold text-gray-900'>{title}</p>}
            <p className='max-w-md whitespace-pre-line text-center text-sm text-gray-500'>{paragraph}</p>
            {children && <div className='mt-2 flex gap-x-3'>{children}</div>}
        </div>
    );
};
